// Movement confirmation buttons component

import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';

/**
 * @typedef {object} Token
 * @property {string} id - Token ID
 * @property {number} col - Column position
 * @property {number} row - Row position
 * @property {string} [size] - Token size (tiny, small, medium, large, huge, gargantuan)
 */

/**
 * Movement confirmation buttons component
 * Displays accept/cancel buttons positioned near a token for move confirmation
 */
class MovementConfirmationButtons extends BaseComponent {
  static properties = {
    token: { type: Object },
    gridSize: { type: Number, attribute: 'grid-size' },
    zoom: { type: Number },
    panX: { type: Number, attribute: 'pan-x' },
    panY: { type: Number, attribute: 'pan-y' },
  };

  static styles = css`
    :host {
      display: block;
    }

    .move-confirmation-buttons {
      position: absolute;
      display: flex;
      gap: 8px;
      z-index: 1000;
      pointer-events: auto;
    }

    .confirm-button {
      width: 36px;
      height: 36px;
      border-radius: 6px;
      border: 2px solid;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.15s ease;
      padding: 0;
    }

    .confirm-button:hover {
      transform: scale(1.1);
    }

    .accept-button {
      background-color: #22c55e;
      border-color: #16a34a;
      color: white;
    }

    .accept-button:hover {
      background-color: #16a34a;
    }

    .cancel-button {
      background-color: #ef4444;
      border-color: #dc2626;
      color: white;
    }

    .cancel-button:hover {
      background-color: #dc2626;
    }
  `;

  constructor() {
    super();
    /** @type {Token|null} */
    this.token = null;
    this.gridSize = 70;
    this.zoom = 1;
    this.panX = 0;
    this.panY = 0;
  }

  /** @param {string} size */
  _getTokenSizeMultiplier(size) {
    switch ((size || 'medium').toLowerCase()) {
      case 'tiny':
        return 0.5;
      case 'small':
        return 1;
      case 'medium':
        return 1;
      case 'large':
        return 2;
      case 'huge':
        return 3;
      case 'gargantuan':
        return 4;
      default:
        return 1;
    }
  }

  _handleAccept() {
    this.dispatchEvent(new CustomEvent('accept', { bubbles: true }));
  }

  _handleCancel() {
    this.dispatchEvent(new CustomEvent('cancel', { bubbles: true }));
  }

  _renderCheckIcon() {
    return html`
      <svg
        width="20"
        height="20"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M20 6 9 17l-5-5" />
      </svg>
    `;
  }

  _renderXIcon() {
    return html`
      <svg
        width="20"
        height="20"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M18 6 6 18" />
        <path d="m6 6 12 12" />
      </svg>
    `;
  }

  render() {
    if (!this.token) {
      return html``;
    }

    const sizeMultiplier = this._getTokenSizeMultiplier(this.token.size || 'medium');
    const left = (this.token.col * this.gridSize + (this.gridSize * sizeMultiplier) / 2) * this.zoom + this.panX + 70;
    const top = this.token.row * this.gridSize * this.zoom + this.panY - 25;

    return html`
      <div class="move-confirmation-buttons" style="left: ${left}px; top: ${top}px;">
        <button class="confirm-button accept-button" title="Accept move" @click="${this._handleAccept}">
          ${this._renderCheckIcon()}
        </button>
        <button class="confirm-button cancel-button" title="Cancel move" @click="${this._handleCancel}">
          ${this._renderXIcon()}
        </button>
      </div>
    `;
  }
}

customElements.define('movement-confirmation-buttons', MovementConfirmationButtons);
